/**
 * The game detail Odds tab (ISSUE-985, ISSUE-1086): the game's line as one
 * sportsbook-style table. A row per team, away first, with Spread (or the
 * sport's own name for it), Total and Money columns; the away row carries the
 * Over and the home row the Under. After a final the server's own results tag
 * the cell that covered, won or hit, and a push tags both cells of its column.
 * Line data is read only context for a pick (say-no.md line 11): nothing here
 * is a control, and the book the line came from is never named (Q260, Q261).
 * A market with no data reads "No line yet" in its own cells, so one missing
 * market never blanks the others, and a game with no line at all still shows
 * the table.
 */
import type { ReactNode } from "react";

import {
  buildOddsTable,
  MONEY_COLUMN_LABEL,
  NO_LINE_COPY,
  ODDS_REFERENCE_NOTE,
  PUSH_TAG,
  TEAM_COLUMN_LABEL,
  TOTAL_LABEL,
  type typeEventOdds,
  type typeOddsCell,
} from "./oddsLogic";
import { FittedTeamName } from "./FittedTeamName";
import { TeamMark } from "./TeamMark";
import { resolvePlatePair } from "./teamPlateText";
import { GameSectionCard } from "./GameSectionCard";

export type typeOddsTabTeam = {
  teamId: string;
  name: string;
  abbreviation: string;
  logoUrl: string | null;
  /** The nickname ("Panthers"), shown when the full name does not fit. */
  shortName?: string | null;
};

/** text-body, the size the team name renders at. */
const TEAM_NAME_FONT_PX = 16;

const VALUE_COLUMN_CLASS = "w-16 sm:w-24";

function OddsCell({
  market,
  cell,
}: {
  market: "spread" | "total" | "money";
  cell: typeOddsCell | null;
}): ReactNode {
  // h-px on the cell lets the box's h-full resolve to the row's height, so a
  // row's three boxes match even when only one carries a result tag.
  return (
    <td className="h-px p-0 align-middle" data-testid={`odds-cell-${market}`}>
      {/* Spec 7.6: a 44 tall sunken price cell, radius 8, the line in
          caption 600 over the price in small. */}
      <div className="flex h-full min-h-touch-target flex-col items-center justify-center rounded-sm bg-surface-sunken p-1 text-center">
        {cell === null ? (
          <span
            className="text-caption text-ink-secondary"
            data-testid="odds-cell-empty"
          >
            {NO_LINE_COPY}
          </span>
        ) : (
          <>
            {cell.line === null ? null : (
              <span
                className="text-caption tabular-nums text-ink"
                data-testid="odds-line"
              >
                {cell.line}
              </span>
            )}
            {cell.price === null ? null : (
              <span
                className={
                  cell.line === null
                    ? "text-caption tabular-nums text-ink"
                    : "text-small tabular-nums text-ink-secondary"
                }
                data-testid="odds-price"
              >
                {cell.price}
              </span>
            )}
            {cell.tag === null ? null : (
              <span
                className={
                  cell.tag === PUSH_TAG
                    ? "rounded-md px-1 text-caption bg-surface-sunken text-ink-secondary"
                    : "rounded-md px-1 text-caption bg-success-tint text-success"
                }
                data-testid="odds-result"
              >
                {cell.tag}
              </span>
            )}
          </>
        )}
      </div>
    </td>
  );
}

export const OddsTab = ({
  odds,
  home,
  away,
}: {
  odds: typeEventOdds;
  home: typeOddsTabTeam;
  away: typeOddsTabTeam;
}): ReactNode => {
  const { awayText, homeText } = resolvePlatePair({ away, home });
  const teamsBySide = {
    away: { ...away, plateText: awayText },
    home: { ...home, plateText: homeText },
  };
  const table = buildOddsTable({
    odds,
    homeTeamId: home.teamId,
    awayTeamId: away.teamId,
  });

  return (
    <GameSectionCard testId="odds-tab" title="Odds">
      <p
        className="text-caption text-ink-tertiary"
        data-testid="odds-reference-note"
      >
        {ODDS_REFERENCE_NOTE}
      </p>
      <table
        data-testid="odds-table"
        className="w-full table-fixed border-separate border-spacing-1"
      >
        <caption className="sr-only">{`${away.name} at ${home.name} odds`}</caption>
        <thead>
          <tr>
            <th scope="col" className="p-0">
              <span className="sr-only">{TEAM_COLUMN_LABEL}</span>
            </th>
            {[table.spreadLabel, TOTAL_LABEL, MONEY_COLUMN_LABEL].map(
              (label) => (
                <th
                  key={label}
                  scope="col"
                  className={`${VALUE_COLUMN_CLASS} p-0 text-center text-caption font-normal text-ink-secondary`}
                  data-testid="odds-column-header"
                >
                  {label}
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row) => {
            const team = teamsBySide[row.side];
            return (
              <tr key={row.side} data-testid={`odds-row-${row.side}`}>
                <th scope="row" className="p-0 text-left font-normal">
                  <span className="flex min-w-0 items-center gap-2">
                    <TeamMark
                      name={team.name}
                      abbreviation={team.plateText}
                      logoUrl={team.logoUrl}
                      size="sm"
                    />
                    <FittedTeamName
                      name={team.name}
                      shortName={team.shortName ?? null}
                      abbreviation={team.abbreviation}
                      fontPx={TEAM_NAME_FONT_PX}
                      className="text-body text-ink"
                      testId="odds-team-name"
                    />
                  </span>
                </th>
                <OddsCell market="spread" cell={row.spread} />
                <OddsCell market="total" cell={row.total} />
                <OddsCell market="money" cell={row.money} />
              </tr>
            );
          })}
        </tbody>
      </table>
    </GameSectionCard>
  );
};
